/**
 * Formats a decade key into its display label, e.g. "2010" -> "2010-talet".
 *
 * @param {string|number} year
 * @returns {string}
 */
export function formatDecade(year) {
  const suffix = globalThis.lavendlaObituary?.common?.decadeSuffix || "-talet";

  return `${year}${suffix}`;
}

/** A year before this, or in the future, counts as no year. */
const FIRST_YEAR = 1850;

/**
 * Filters images to only include those with a valid year set: not taken off the timeline
 * by the family, and not before 1850 or in the future.
 *
 * @param {Array} images
 * @returns {Array}
 */
export function filterImagesWithYear(images) {
  if (!Array.isArray(images)) {
    return [];
  }

  const now = new Date().getFullYear();
  return images.filter((image) => {
    if (!image.groupedYearTaken || image.timelineHidden) {
      return false;
    }
    const year = Number(image.yearTaken);
    return !year || (year >= FIRST_YEAR && year <= now);
  });
}

/**
 * Groups images from comments by decade, excluding images without a year.
 * Returns sorted array of { year, images } objects.
 *
 * @param {Array} comments
 * @returns {Array<{year: string, images: Array}>}
 */
export function groupMediaImagesByYear(comments) {
  const yearMap = {};

  for (const comment of comments) {
    if (!comment.mediaImages) {
      continue;
    }

    const imagesWithYear = filterImagesWithYear(comment.mediaImages);

    for (const image of imagesWithYear) {
      const yearKey = image.groupedYearTaken;

      if (!yearMap[yearKey]) {
        yearMap[yearKey] = [];
      }

      yearMap[yearKey].push({ ...image, commentId: comment.id });
    }
  }

  return Object.keys(yearMap)
    .sort((a, b) => Number(a) - Number(b))
    .map((year) => ({
      images: yearMap[year].sort(
        (a, b) => Number(a.yearTaken || 0) - Number(b.yearTaken || 0)
      ),
      year,
    }));
}

/** The timeline tab needs at least this many images with a year... */
export const MIN_TIMELINE_IMAGES = 3;
/** ...spread over at least this many decades. */
export const MIN_TIMELINE_DECADES = 2;

/**
 * Whether grouped timeline images are enough to show the timeline: at least
 * 3 images covering at least 2 different decades.
 *
 * @param {Array<{year: string, images: Array}>} groups Result of groupMediaImagesByYear
 * @returns {boolean}
 */
export function hasEnoughTimelineImages(groups) {
  if (!Array.isArray(groups)) {
    return false;
  }

  const decades = groups.filter(({ images }) => images.length > 0).length;
  const total = groups.reduce((sum, { images }) => sum + images.length, 0);

  return total >= MIN_TIMELINE_IMAGES && decades >= MIN_TIMELINE_DECADES;
}
